import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  MessageSquare,
  Send,
  X,
  Minimize,
  Maximize,
  Video,
  CloudUpload,
  FolderOpen,
  FileText,
  FilePdf,
  FileImage,
  AlertTriangle,
  CheckCircle,
  Loader,
  Cloud
} from "lucide-react";
import VideoAnalytics from "../VideoAnalytics";
import { marked } from "marked";
import config from "../../utils/config";
import { getSelectedSport, getSelectedTeam, getUser, successMessage, errorMessage, uploadTrainingVideos, getSelectedTeamDetail, formatFileSize } from "../../utils/helpers";
import apiClient from "../../utils/apiClient";
import { useTheme } from "../../utils/theme";
import ImgDrive from '../../assets/drive.png'
import ImgOneDrive from '../../assets/ondrive.png'
import { useMsalAuth } from '../MsalProvider';
import OneDriveService from '../../utils/oneDriveService';
import OneDrivePicker from '../OneDrivePicker';
import { DEVELOPER_KEY, googleClientId as CLIENT_ID } from "../../utils/constant";

// Google Drive Picker temporarily disabled - package requires private GitHub auth
// import useDrivePicker from '@aretec-inc/di-google-drive-picker';
const useDrivePicker = () => {
  const openPicker = (config) => {
    console.warn('Google Drive Picker is temporarily disabled. Please use local file upload or OneDrive.');
    if (config?.callbackFunction) {
      config.callbackFunction({ action: 'cancel' });
    }
  };
  return [openPicker, null, false];
};

const UploadTrainingStats = ({ uploadTraining, setUploadTraining }) => {
  const { theme: appTheme } = useTheme();
  const isDarkMode = true; // OLED: always dark

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const { instance, isAuthenticated, isInitialized, clearAuthState, forceNewLogin } = useMsalAuth();
  const [oneDriveService, setOneDriveService] = useState(null);
  const [isDownloadingOneDriveFiles, setIsDownloadingOneDriveFiles] = useState(false);
  const [isDownloadingGoogleDriveFiles, setIsDownloadingGoogleDriveFiles] = useState(false);

  // Configure Google Drive Picker
  const [openPicker, authResponse, googleApiLoaded] = useDrivePicker();
  console.log('uploadedFiles', uploadedFiles);

  useEffect(() => {
    if (authResponse?.access_token) {
      sessionStorage.setItem('authToken', authResponse?.access_token);
    }
  }, [authResponse]);

  // Utility function to validate and refresh Google Drive token
  const validateGoogleToken = async () => {
    const token = sessionStorage.getItem('authToken');
    if (!token) {
      throw new Error('No access token available for Google Drive. Please try again.');
    }

    try {
      // Test token validity with a simple API call
      const testResponse = await fetch('https://www.googleapis.com/oauth2/v1/tokeninfo?access_token=' + token);
      if (!testResponse.ok) {
        throw new Error('Invalid or expired access token');
      }
      
      const tokenInfo = await testResponse.json();
      
      // Check if token has required scopes
      if (!tokenInfo.scope.includes('drive.readonly') && !tokenInfo.scope.includes('drive')) {
        throw new Error('Token does not have required Google Drive permissions');
      }
      
      return token;
    } catch (error) {
      sessionStorage.removeItem('authToken');
      throw new Error('Authentication failed. Please try selecting files again.');
    }
  };

  const handleGoogleDriveClick = () => {
    // Check if adding more files would exceed the 5 file limit
    const remainingSlots = 5 - uploadedFiles.length;
    if (remainingSlots <= 0) {
      setError("Maximum file limit reached (5 files). Please remove some files before adding more.");
      return;
    }

    // Configure picker options with proper scopes for file downloading
    const pickerConfig = {
      clientId: CLIENT_ID,
      developerKey: DEVELOPER_KEY,
      appId: CLIENT_ID.split('-')[0],
      viewId: "DOCS",
      multiselect: true,
      // Only allow the specified file types
      mimeTypes: [
        'image/png',
        'image/jpeg',
        'image/jpg',
        'application/pdf'
      ],

      // CRITICAL: Request proper scopes for file downloading
      scopes: [
        'https://www.googleapis.com/auth/drive.readonly',
        'https://www.googleapis.com/auth/drive.file',
        'https://www.googleapis.com/auth/drive.metadata.readonly'
      ],

      // Enable shared drives and multiple selection
      supportDrives: true,
      multiSelect: true,
      maxItems: remainingSlots,

      // Disable folder selection
      setSelectFolderEnabled: false,
      setIncludeFolders: false,

      // Request essential fields for download
      fields: 'id,name,mimeType,sizeBytes,iconUrl,webViewLink,webContentLink',

      // Set callback function
      callbackFunction: (data) => {
        if (data.action === 'cancel') {
          console.log('User canceled picker selection');
          return;
        }

        if (data.action === 'picked') {
          console.log('Google Drive picked data:', JSON.stringify(data));
          handleGoogleDriveSelect(data);
        }
      }
    };

    // Guard: check if API key is configured
    if (!DEVELOPER_KEY) {
      console.warn('[Google Picker] REACT_APP_GOOGLE_PICKER_KEY not configured');
      setError('Google Drive Picker is not configured. Please set the REACT_APP_GOOGLE_PICKER_KEY environment variable.');
      return;
    }

    // Open the picker
    openPicker(pickerConfig);
  };

  // Handle Google Drive file selection with proper API usage
  const handleGoogleDriveSelect = (data) => {
    console.log('Google Drive selection data:', data);
    console.log('Google Drive picked docs:', JSON.stringify(data?.docs));

    if (!data?.docs || data.docs.length === 0) {
      return;
    }

    // Check if adding these files would exceed the 5 file limit
    const remainingSlots = 5 - uploadedFiles.length;
    if (remainingSlots <= 0) {
      setError("Maximum file limit reached (5 files). Please remove some files before adding more.");
      return;
    }

    // Take only as many files as we have slots for
    const filesToProcess = data.docs.slice(0, remainingSlots);

    // If there are more files than slots, add a warning
    if (data.docs.length > remainingSlots) {
      setError(`Only ${remainingSlots} more file${remainingSlots === 1 ? '' : 's'} can be added. The first ${remainingSlots} file${remainingSlots === 1 ? '' : 's'} will be processed.`);
    }

    setIsDownloadingGoogleDriveFiles(true);

    const selectedFiles = filesToProcess;
    const invalidFiles = [];

    // Validate file types
    const validGoogleDriveFiles = selectedFiles.filter(file => {
      const extension = file.name.split('.').pop().toLowerCase();
      const allowedExtensions = ['png', 'jpg', 'jpeg', 'pdf'];

      if (!allowedExtensions.includes(extension)) {
        invalidFiles.push(`File "${file.name}" has an unsupported file type. Only PNG, JPG, JPEG, and PDF files are allowed.`);
        return false;
      }

      return true;
    });

    if (invalidFiles.length > 0) {
      setError(invalidFiles.join('\n'));
      if (validGoogleDriveFiles.length === 0) {
        setIsDownloadingGoogleDriveFiles(false);
        return;
      }
    }

    // Process Google Drive files in parallel
    const downloadAndProcessFiles = async () => {
      const totalFiles = validGoogleDriveFiles.length;
      let completedFiles = 0;

      // Function to download a single file using proper Google Drive API
      const downloadFile = async (doc) => {
        try {
          // Create a file representation with download status
          const fileId = Date.now() + Math.random();
          const fileInfo = {
            id: fileId,
            name: doc.name,
            size: formatFileSize(doc.sizeBytes ? parseInt(doc.sizeBytes) : 0),
            rawSize: doc.sizeBytes ? parseInt(doc.sizeBytes) : 0,
            type: getFileType(doc.name) === 'image' ? 'Image' :
              getFileType(doc.name) === 'pdf' ? 'PDF' :
                getFileType(doc.name) === 'csv' ? 'CSV Format' : 'File',
            modified: 'Just now',
            status: 'processing',
            progress: 0,
            fileType: getFileType(doc.name),
            isGoogleDrive: true,
            googleDriveId: doc.id
          };

          // Add to upload list
          setUploadedFiles(prev => [...prev, fileInfo]);

          // Create a progress updater for this file
          let progressUpdateCount = 0;
          const updateDownloadProgress = (progressPercent) => {
            // Limit updates to avoid too many renders
            progressUpdateCount++;
            if (progressUpdateCount % 3 !== 0 && progressPercent < 100) return;

            setUploadedFiles(prev => prev.map(f =>
              f.id === fileId ? {
                ...f,
                progress: Math.min(progressPercent, 95),
                status: 'processing'
              } : f
            ));
          };

          // Start with initial progress
          updateDownloadProgress(10);

          // Download file using proper Google Drive API
          const downloadWithProgress = async () => {
            try {
              console.log('Downloading file:', doc.name, 'with ID:', doc.id);

              // Validate token first
              const accessToken = await validateGoogleToken();
              
              updateDownloadProgress(20);

              // First, get file metadata to verify access and get proper MIME type
              console.log('Fetching file metadata...');
              const metadataResponse = await fetch(
                `https://www.googleapis.com/drive/v3/files/${doc.id}?fields=id,name,mimeType,size,parents&supportsAllDrives=true`, 
                {
                  headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Accept': 'application/json'
                  }
                }
              );

              if (!metadataResponse.ok) {
                const errorText = await metadataResponse.text();
                console.error('Metadata request failed:', errorText);
                
                if (metadataResponse.status === 403) {
                  throw new Error(`Access denied to "${doc.name}". The file might be restricted or you may not have permissions.`);
                } else if (metadataResponse.status === 404) {
                  throw new Error(`File "${doc.name}" not found. It may have been deleted or moved.`);
                } else if (metadataResponse.status === 401) {
                  throw new Error('Authentication failed. Please try selecting files again.');
                }
                
                throw new Error(`Failed to access file metadata: ${metadataResponse.status} ${metadataResponse.statusText}`);
              }

              const metadata = await metadataResponse.json();
              console.log('File metadata retrieved:', metadata);
              
              updateDownloadProgress(35);

              // Now download the actual file content using the Google Drive API
              console.log('Starting file download...');
              const downloadUrl = `https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media&supportsAllDrives=true`;
              
              const response = await fetch(downloadUrl, {
                headers: {
                  'Authorization': `Bearer ${accessToken}`,
                  'Accept': '*/*'
                }
              });

              if (!response.ok) {
                const errorText = await response.text();
                console.error('Download error response:', errorText);
                
                if (response.status === 403) {
                  throw new Error(`Download denied for "${doc.name}". Check file permissions and API quotas.`);
                } else if (response.status === 404) {
                  throw new Error(`File "${doc.name}" not found during download.`);
                } else if (response.status === 401) {
                  throw new Error('Authentication expired during download. Please try again.');
                }
                
                throw new Error(`Download failed: ${response.status} ${response.statusText}`);
              }

              console.log('Download response received, processing stream...');

              // Handle the response stream with progress tracking
              const contentLength = response.headers.get('Content-Length');
              let loaded = 0;
              const total = contentLength ? parseInt(contentLength, 10) : 0;
              const reader = response.body.getReader();
              const chunks = [];

              // Read the response stream
              while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                
                chunks.push(value);
                loaded += value.length;

                // Update progress (35% to 90% range for download)
                if (total > 0) {
                  const downloadProgress = Math.round(35 + (loaded / total) * 55);
                  updateDownloadProgress(downloadProgress);
                } else {
                  // If size is unknown, increment based on data received
                  const estimatedProgress = Math.min(35 + (loaded / (50 * 1024)), 90);
                  updateDownloadProgress(estimatedProgress);
                }
              }

              // Combine chunks into a single Uint8Array
              const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
              const chunksAll = new Uint8Array(totalLength);
              let position = 0;
              for (const chunk of chunks) {
                chunksAll.set(chunk, position);
                position += chunk.length;
              }

              // Create blob with proper MIME type from metadata
              const mimeType = metadata.mimeType || 'application/octet-stream';
              const blob = new Blob([chunksAll], { type: mimeType });
              
              console.log('File download completed:', {
                name: doc.name,
                size: blob.size,
                type: mimeType
              });
              
              updateDownloadProgress(95);

              return blob;
            } catch (error) {
              console.error('Error in downloadWithProgress:', error);
              throw error;
            }
          };

          // Execute the download with progress tracking
          let blob;
          try {
            blob = await downloadWithProgress();
          } catch (error) {
            console.error('Download failed for', doc.name, ':', error);
            throw error;
          }

          if (!blob || blob.size === 0) {
            throw new Error(`Failed to download ${doc.name} from Google Drive - empty file received`);
          }

          // Create a real file object with the downloaded content
          const realFile = new File([blob], doc.name, {
            type: blob.type
          });

          // Update file with the actual file object and completed status
          setUploadedFiles(prev => prev.map(f =>
            f.id === fileId ? {
              ...f,
              file: realFile,
              status: 'ready',
              progress: 100
            } : f
          ));

          // Update completed count
          completedFiles++;

          console.log(`Successfully downloaded ${doc.name} (${completedFiles}/${totalFiles})`);

          return { success: true };
        } catch (error) {
          console.error('Error processing Google Drive file:', error);
          
          // Update file status to error
          setUploadedFiles(prev => prev.map(f =>
            f.name === doc.name && f.isGoogleDrive ? {
              ...f,
              status: 'error',
              progress: 0
            } : f
          ));

          // Set error message
          setError(prev => {
            const newError = `Error downloading ${doc.name}: ${error.message}`;
            return prev ? prev + '\n' + newError : newError;
          });

          return { success: false, error };
        }
      };

      try {
        // Process files sequentially to avoid hitting API rate limits
        for (const doc of validGoogleDriveFiles) {
          await downloadFile(doc);
          // Small delay between downloads to be respectful to the API
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        // Show final success message
        const successfulDownloads = completedFiles;
        if (successfulDownloads > 0) {
          setSuccessMsg(`Successfully loaded ${successfulDownloads} of ${totalFiles} files from Google Drive`);
          setTimeout(() => setSuccessMsg(null), 5000);
        }

      } catch (error) {
        console.error('Batch download error:', error);
        setError(`Failed to download files: ${error.message}`);
      } finally {
        setIsDownloadingGoogleDriveFiles(false);
      }
    };

    // Start downloading and processing files
    downloadAndProcessFiles();
  };

  // Initialize OneDrive service when authenticated
  useEffect(() => {
    if (instance && isInitialized && isAuthenticated) {
      try {
        console.log('Creating OneDriveService in UploadTrainingStats');
        const service = new OneDriveService(instance);
        setOneDriveService(service);
      } catch (error) {
        console.error('Failed to create OneDriveService:', error);
      }
    }
  }, [instance, isInitialized, isAuthenticated]);

  // Get OneDrive token silently when already authenticated
  useEffect(() => {
    if (isAuthenticated && instance && isInitialized && oneDriveService) {
      try {
        console.log('Attempting to get OneDrive token silently');
        oneDriveService.getTokenSilently()
          .then(token => {
            if (token) {
              sessionStorage.setItem('oneDriveToken', token);
              console.log('OneDrive token saved successfully');
            } else {
              console.warn('No token returned from silent authentication');
              clearAuthState();
            }
          })
          .catch(error => {
            console.error('Silent token acquisition failed:', error);
            if (error.message && (error.message.includes('No active account') ||
              error.message.includes('not initialized') ||
              error.message.includes('login required'))) {
              console.log('Authentication issue detected, clearing auth state');
              clearAuthState();
            }
          });
      } catch (error) {
        console.error('Error in OneDrive silent authentication:', error);
        clearAuthState();
      }
    }
  }, [isAuthenticated, instance, isInitialized, oneDriveService, clearAuthState]);

  const toggleChat = () => {
    setUploadTraining(false);
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileSelection(files);
  }, []);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileType = (fileName) => {
    const extension = fileName?.split('.')?.pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(extension)) return 'image';
    if (['pdf'].includes(extension)) return 'pdf';
    return 'file';
  };

  const validateFile = (file) => {
    // Check file size (25MB limit)
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
    if (file.size > MAX_FILE_SIZE) {
      return `File "${file.name}" is too large. Maximum file size is 25MB.`;
    }

    // Check file type
    const extension = file.name.split('.').pop().toLowerCase();
    const allowedExtensions = ['png', 'jpg', 'jpeg', 'pdf'];
    if (!allowedExtensions.includes(extension)) {
      return `File "${file.name}" has an unsupported file type. Only PNG, JPG, JPEG, and PDF files are allowed.`;
    }

    return null; // No error
  };

  const handleFileSelection = (files) => {
    // Clear any previous errors
    setError(null);

    // Check if adding these files would exceed the 5 file limit
    const remainingSlots = 5 - uploadedFiles.length;
    if (remainingSlots <= 0) {
      setError("Maximum file limit reached (5 files). Please remove some files before adding more.");
      return;
    }

    // Take only as many files as we have slots for
    const filesToProcess = Array.from(files).slice(0, remainingSlots);

    // If there are more files than slots, add a warning
    if (files.length > remainingSlots) {
      setError(`Only ${remainingSlots} more file${remainingSlots === 1 ? '' : 's'} can be added. The first ${remainingSlots} file${remainingSlots === 1 ? '' : 's'} will be processed.`);
    }

    // Validate files first
    const validFiles = [];
    const errors = [];

    filesToProcess.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(error);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      setError((prev) => prev ? prev + '\n' + errors.join('\n') : errors.join('\n'));
      // If there are some valid files, we can still proceed with those
      if (validFiles.length === 0) {
        return;
      }
    }

    const newFiles = validFiles.map((file, index) => ({
      id: Date.now() + index,
      file: file, // Store the actual file object
      name: file.name,
      size: formatFileSize(file.size),
      rawSize: file.size,
      type: file.type.includes('image') ? 'Image' :
        file.type.includes('pdf') ? 'PDF' :
          file.type.includes('csv') ? 'CSV Format' : 'File',
      modified: 'Just now',
      status: 'ready', // Local files are immediately ready
      progress: 100,
      fileType: getFileType(file.name),
      isOneDrive: false,
      isGoogleDrive: false
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  // Handle OneDrive file selection
  const handleOneDriveSelect = (data) => {
    console.log('OneDrive selection data:', data);

    if (data?.action === 'cancel') {
      return;
    }

    if (data?.action === 'error') {
      setError(data.message || 'Error connecting to OneDrive');
      return;
    }

    if (data?.docs) {
      // Check if adding these files would exceed the 5 file limit
      const remainingSlots = 5 - uploadedFiles.length;
      if (remainingSlots <= 0) {
        setError("Maximum file limit reached (5 files). Please remove some files before adding more.");
        return;
      }

      // Take only as many files as we have slots for
      const filesToProcess = data.docs.slice(0, remainingSlots);

      // If there are more files than slots, add a warning
      if (data.docs.length > remainingSlots) {
        setError(`Only ${remainingSlots} more file${remainingSlots === 1 ? '' : 's'} can be added. The first ${remainingSlots} file${remainingSlots === 1 ? '' : 's'} will be processed.`);
      }

      setIsDownloadingOneDriveFiles(true);

      const selectedFiles = filesToProcess;
      const invalidFiles = [];

      // Validate file types
      const validOneDriveFiles = selectedFiles.filter(file => {
        const extension = file.name.split('.').pop().toLowerCase();
        const allowedExtensions = ['png', 'jpg', 'jpeg', 'pdf'];

        if (!allowedExtensions.includes(extension)) {
          invalidFiles.push(`File "${file.name}" has an unsupported file type. Only PNG, JPG, JPEG, and PDF files are allowed.`);
          return false;
        }

        return true;
      });

      if (invalidFiles.length > 0) {
        setError(invalidFiles.join('\n'));
        if (validOneDriveFiles.length === 0) {
          setIsDownloadingOneDriveFiles(false);
          return;
        }
      }

      // Process OneDrive files in parallel
      const downloadAndProcessFiles = async () => {
        const totalFiles = validOneDriveFiles.length;
        let completedFiles = 0;

        // Function to download a single file
        const downloadFile = async (doc) => {
          try {
            // Create a file representation with download status
            const fileId = Date.now() + Math.random();
            const fileInfo = {
              id: fileId,
              name: doc.name,
              size: formatFileSize(doc.size || 0),
              rawSize: doc.size || 0,
              type: getFileType(doc.name) === 'image' ? 'Image' :
                getFileType(doc.name) === 'pdf' ? 'PDF' :
                  getFileType(doc.name) === 'csv' ? 'CSV Format' : 'File',
              modified: 'Just now',
              status: 'processing',
              progress: 0,
              fileType: getFileType(doc.name),
              isOneDrive: true,
              oneDriveId: doc.id
            };

            // Add to upload list
            setUploadedFiles(prev => [...prev, fileInfo]);

            // Create a progress updater for this file
            let progressUpdateCount = 0;
            const updateDownloadProgress = (progressPercent) => {
              // Limit updates to avoid too many renders
              progressUpdateCount++;
              if (progressUpdateCount % 3 !== 0 && progressPercent < 100) return;

              setUploadedFiles(prev => prev.map(f =>
                f.id === fileId ? {
                  ...f,
                  progress: Math.min(progressPercent, 95), // Cap at 95% until final processing
                  status: 'processing'
                } : f
              ));
            };

            // Simulate initial progress (for user feedback)
            updateDownloadProgress(10);

            // Download file with XMLHttpRequest to track progress
            const downloadWithProgress = () => {
              return new Promise((resolve, reject) => {
                // Get the download URL from OneDrive service
                oneDriveService.getDownloadUrl(doc.id)
                  .then(downloadUrl => {
                    if (!downloadUrl) {
                      throw new Error(`Failed to get download URL for ${doc.name}`);
                    }

                    // Set up XHR for progress tracking
                    const xhr = new XMLHttpRequest();
                    xhr.open('GET', downloadUrl, true);
                    xhr.responseType = 'blob';

                    // Track download progress
                    xhr.onprogress = (event) => {
                      if (event.lengthComputable) {
                        const progressPercent = Math.round((event.loaded / event.total) * 90);
                        updateDownloadProgress(progressPercent);
                      }
                    };

                    xhr.onload = () => {
                      if (xhr.status === 200) {
                        resolve(xhr.response);
                      } else {
                        reject(new Error(`Download failed with status ${xhr.status}`));
                      }
                    };

                    xhr.onerror = () => {
                      reject(new Error('Network error during download'));
                    };

                    xhr.send();
                  })
                  .catch(reject);
              });
            };

            // Execute the download with progress tracking
            let blob;
            try {
              blob = await downloadWithProgress();
              updateDownloadProgress(95);
            } catch (error) {
              // Fall back to regular download if progress tracking fails
              console.warn('Progress tracking download failed, falling back to regular download', error);
              blob = await oneDriveService.downloadFile(doc.id);
            }

            if (!blob) {
              throw new Error(`Failed to download ${doc.name} from OneDrive`);
            }

            // Create a real file object with the downloaded content
            const realFile = new File([blob], doc.name, {
              type: doc.mimeType || oneDriveService.getMimeTypeFromName(doc.name)
            });

            // Update file with the actual file object and completed status
            setUploadedFiles(prev => prev.map(f =>
              f.id === fileId ? {
                ...f,
                file: realFile,
                status: 'ready',
                progress: 100
              } : f
            ));

            // Update completed count
            completedFiles++;

            // Show success message
            setSuccessMsg(`Successfully loaded ${completedFiles} of ${totalFiles} files from OneDrive`);

            // Clear success message after a delay
            setTimeout(() => setSuccessMsg(null), 3000);

            return { success: true };
          } catch (error) {
            console.error('Error processing OneDrive file:', error);
            setError(`Error downloading ${doc.name}: ${error.message}`);

            // Add error file to the list
            setUploadedFiles(prev => prev.map(f =>
              f.name === doc.name && f.isOneDrive ? {
                ...f,
                status: 'error',
                progress: 0
              } : f
            ));

            return { success: false, error };
          }
        };

        // Process files in batches of 3 for controlled parallelism
        const MAX_CONCURRENT_DOWNLOADS = 3;
        for (let i = 0; i < validOneDriveFiles.length; i += MAX_CONCURRENT_DOWNLOADS) {
          const batch = validOneDriveFiles.slice(i, i + MAX_CONCURRENT_DOWNLOADS);
          await Promise.all(batch.map(doc => downloadFile(doc)));
        }

        setIsDownloadingOneDriveFiles(false);
      };

      // Start downloading and processing files
      downloadAndProcessFiles();
    }
  };

  // Handle final upload submission
  const handleUploadSubmit = async () => {
    setIsUploading(true);
    setError(null);
    setSuccessMsg(null);

    // Get files that are ready
    const filesToUpload = uploadedFiles?.filter(file =>
      file.status === 'ready' && file.file
    );

    if (filesToUpload?.length === 0) {
      setError("No files are ready to upload. Please wait for downloads to complete or add local files.");
      setIsUploading(false);
      return;
    }

    // Set all files to processing state
    setUploadedFiles(prev => prev.map(f =>
      filesToUpload.some(file => file.id === f.id)
        ? { ...f, status: 'processing', progress: 0 }
        : f
    ));

    // Define progress callback function
    const updateProgress = (fileId, progress, errorMsg = null) => {
      if (progress === -1 && errorMsg) {
        // Handle error case
        setUploadedFiles(prev => prev.map(f =>
          f.id === fileId ? { ...f, status: 'error', progress: 0 } : f
        ));
        setError(`Error uploading ${uploadedFiles.find(f => f.id === fileId)?.name}: ${errorMsg}`);
      } else {
        // Update progress
        setUploadedFiles(prev => prev.map(f =>
          f.id === fileId ? {
            ...f,
            progress,
            status: progress === 100 ? 'ready' : 'processing'
          } : f
        ));
      }
    };

    try {
      // Call the uploadTrainingVideos function with the progress callback
      const results = await uploadTrainingVideos(filesToUpload, updateProgress);

      // Handle results
      const successful = results.filter(r => r?.success).length;
      const failed = results.filter(r => !r?.success).length;

      if (failed > 0) {
        if (successful > 0) {
          successMessage(`${successful} files uploaded successfully. ${failed} files failed.`);
        } else {
          errorMessage('Upload failed. Please try again.');
        }
      } else {
        successMessage('Upload complete. Your files are being processed.');
        // Clear the file list on successful upload
        setUploadedFiles([]);
        setUploadTraining(false)
      }
    } catch (error) {
      console.error('Upload error:', error);
      errorMessage('Upload failed: ' + (error.message || 'Unknown error'));
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready':
        return 'text-green-600';
      case 'processing':
        return 'text-yellow-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'ready':
        return 'Ready';
      case 'processing':
        return 'Processing...';
      case 'error':
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  const getFileIcon = (fileType, isOneDrive = false, isGoogleDrive = false) => {
    const baseClassName = "w-10 h-10 flex-shrink-0";

    let icon;
    switch (fileType) {
      case 'image':
        icon = <div className={`${baseClassName} bg-gradient-to-br from-orange-500 to-orange-600 rounded flex items-center justify-center text-white font-bold text-xs`}>IMG</div>;
        break;
      case 'pdf':
        icon = <div className={`${baseClassName} bg-gradient-to-br from-red-500 to-red-600 rounded flex items-center justify-center text-white font-bold text-xs`}>PDF</div>;
        break;
      default:
        icon = <div className={`${baseClassName} bg-gradient-to-br from-blue-500 to-blue-600 rounded flex items-center justify-center text-white font-bold text-xs`}>FILE</div>;
    }

    // If it's an OneDrive file, add the OneDrive badge
    if (isOneDrive) {
      return (
        <div className="relative">
          {icon}
          <div className="absolute -top-1 -right-1 bg-blue-600 rounded-full p-1">
            <Cloud className="w-4 h-4 text-white" />
          </div>
        </div>
      );
    }

    // If it's a Google Drive file, add the Google Drive badge
    if (isGoogleDrive) {
      return (
        <div className="relative">
          {icon}
          <div className="absolute -top-1 -right-1 bg-green-600 rounded-full p-1">
            <Cloud className="w-4 h-4 text-white" />
          </div>
        </div>
      );
    }

    return icon;
  };

  return (
    <div
      className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-end justify-center"
    >
      {/* Centered modal */}
      <div
        className="relative bottom-3 z-50"
        style={{ height: "90%", minWidth: "50%", maxWidth: '50%' }}
      >
        {uploadTraining && (
          <div
            style={{ minHeight: "90%", height: "100%" }}
            className={`${"bg-gray-800"
              }  rounded-lg shadow-xl overflow-hidden transition-all duration-300 flex flex-col `}
          >
            <div className="themePrimary px-4 py-3 text-white flex justify-between items-center">
              <div className="flex items-center">
                <h3 className="font-medium">Upload Training Stats</h3>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={toggleChat}
                  className="text-white hover:text-[#13294B]"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <div
              style={{ minHeight: "300px", display: 'flex', flexDirection: 'column' }}
              className={` flex-1 p-4 overflow-y-auto ${"bg-gray-800"
                }  min-h-[300px]`}
            >
              {/* Local Files Section */}
              <div style={{ flex: 1 }}>
                <div className="mb-8">
                  <h3 className={`text-base font-semibold mb-4 flex items-center gap-2 ${'text-gray-300'}`}>
                    📁 Local Files
                  </h3>

                  <div
                    className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all duration-300 ${dragOver
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20 shadow-lg transform -translate-y-1'
                      : 'border-gray-600 bg-gray-700/50 hover:border-green-500 hover:bg-green-900/20'
                      } hover:transform hover:-translate-y-1`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="w-14 h-14  mx-auto mb-0 flex items-center justify-center text-2xl text-white">
                      <CloudUpload size={28} className={
                        dragOver
                          ? 'text-blue-400'
                          : 'text-gray-300'
                      } />
                    </div>
                    <div className={`text-lg font-medium mb-2 ${'text-white'}`}>
                      Drag and drop files here
                    </div>
                    <div className={`text-sm ${'text-gray-400'} mb-4 leading-relaxed`}>
                      Or click to browse and select files from your computer
                    </div>
                    <div className="flex gap-2 justify-center flex-wrap">
                      {['PNG', 'JPEG', 'JPG', 'PDF'].map(type => (
                        <span key={type} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${'bg-gray-700 text-gray-300 border-gray-600'}`}>
                          {type}
                        </span>
                      ))}
                    </div>
                    <div className={`text-xs ${'text-gray-500'} mt-3 italic`}>
                      Maximum 5 files
                    </div>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".png,.jpg,.jpeg,.pdf"
                    className="hidden"
                    onChange={(e) => handleFileSelection(Array.from(e.target.files))}
                  />
                </div>

                {/* Divider */}
                <div className="flex items-center my-8">
                  <div className={`flex-1 h-px ${'bg-gray-700'}`}></div>
                  <span className={`px-4 text-sm font-medium ${'text-gray-400'}`}>OR</span>
                  <div className={`flex-1 h-px ${'bg-gray-700'}`}></div>
                </div>

                {/* Cloud Import Section */}
                <div className="mb-8">
                  <h3 className={`text-base font-semibold mb-4 flex items-center gap-2 ${'text-gray-300'}`}>
                    ☁️ Import from Cloud
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {/* OneDrive */}
                    <OneDrivePicker
                      onSelect={handleOneDriveSelect}
                      acceptedFileTypes={['png', 'jpg', 'jpeg', 'pdf']}
                      multiSelect={true}
                      maxSelections={5 - uploadedFiles.length}
                      customButtonRender={(onClick) => (
                        <button
                          onClick={onClick}
                          className={`flex flex-col items-center gap-3 p-6 border-2 rounded-xl transition-all duration-200 ${'border-gray-600 bg-gray-700/50 hover:border-green-500 hover:bg-green-900/20'
                            } hover:transform hover:-translate-y-1 hover:shadow-lg`}
                        >
                          <div className="w-12 h-12 rounded-lg flex items-center justify-center">
                            <img src={ImgOneDrive} alt="OneDrive" />
                          </div>
                          <span
                            className={`text-sm font-medium ${'text-gray-300'}`}
                          >
                            OneDrive
                          </span>
                        </button>
                      )}
                    />
                    {/* Google Drive */}
                    <button
                      onClick={() => handleGoogleDriveClick()}
                      disabled={isDownloadingGoogleDriveFiles}
                      className={`flex flex-col items-center gap-3 p-6 border-2 rounded-xl transition-all duration-200 ${isDownloadingGoogleDriveFiles 
                        ? 'opacity-50 cursor-not-allowed'
                        : 'border-gray-600 bg-gray-700/50 hover:border-green-500 hover:bg-green-900/20'
                        } ${!isDownloadingGoogleDriveFiles ? 'hover:transform hover:-translate-y-1 hover:shadow-lg' : ''}`}
                    >
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center">
                        <img src={ImgDrive} alt="Google Drive" />
                      </div>
                      <span
                        className={`text-sm font-medium ${'text-gray-300'}`}
                      >
                        Google Drive
                      </span>
                    </button>
                  </div>
                </div>

                {/* Error/Success message display */}
                {error && (
                  <div className={`my-4 p-3 border rounded flex items-start ${'bg-red-900 border-red-700 text-red-200'}`}>
                    <AlertTriangle size={24} className="mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      {error.split('\n').map((paragraph, index) => (
                        <p key={index} className={index > 0 ? 'mt-2 text-sm' : 'font-semibold'}>
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {successMsg && (
                  <div className={`my-4 p-3 border rounded flex items-start ${'bg-green-900 border-green-700 text-green-200'}`}>
                    <CheckCircle size={24} className="mr-3 mt-0.5 flex-shrink-0" />
                    <div className="font-semibold">{successMsg}</div>
                  </div>
                )}

                {/* OneDrive Downloads Status */}
                {isDownloadingOneDriveFiles && (
                  <div className={`my-4 p-3 border rounded ${'bg-blue-900/30 border-blue-800 text-blue-300'}`}>
                    <div className="flex items-center mb-2">
                      <Loader size={18} className={`animate-spin mr-2 ${'text-blue-400'}`} />
                      <span className="font-medium">Downloading files from OneDrive...</span>
                    </div>
                    <div className={`text-sm ${'text-blue-400'}`}>
                      Please wait while we download your selected files from OneDrive.
                    </div>
                  </div>
                )}

                {/* Google Drive Downloads Status */}
                {isDownloadingGoogleDriveFiles && (
                  <div className={`my-4 p-3 border rounded ${'bg-green-900/30 border-green-800 text-green-300'}`}>
                    <div className="flex items-center mb-2">
                      <Loader size={18} className={`animate-spin mr-2 ${'text-green-400'}`} />
                      <span className="font-medium">Downloading files from Google Drive...</span>
                    </div>
                    <div className={`text-sm ${'text-green-400'}`}>
                      Please wait while we download your selected files from Google Drive.
                    </div>
                  </div>
                )}

                {/* Uploaded Files Section */}
                {uploadedFiles.length > 0 && (
                  <div className="mb-8">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className={`text-base font-semibold flex items-center gap-2 ${'text-gray-300'}`}>
                        Selected Files
                      </h3>
                      <div className={`text-sm ${'text-gray-400'}`}>
                        {uploadedFiles.length} of 5 files selected ({5 - uploadedFiles.length} remaining)
                      </div>
                    </div>

                    <div className="space-y-3">
                      {uploadedFiles.map((file) => (
                        <div
                          key={file.id}
                          className={`flex items-center gap-4 p-4 border rounded-lg transition-all duration-200 ${'border-gray-700 bg-gray-700/50 hover:bg-gray-600/50'}`}
                        >
                          {getFileIcon(file.fileType, file.isOneDrive, file.isGoogleDrive)}

                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-medium mb-1 ${'text-white'} truncate`}>
                              {file.name}
                            </div>
                            <div className={`flex gap-4 text-xs ${'text-gray-400'}`}>
                              <span>{file.size}</span>
                              <span>{file.type}</span>
                              <span>Modified {file.modified}</span>
                            </div>
                            {file.progress < 100 && file.status === 'processing' && (
                              <div className={`w-full h-1 ${'bg-gray-600'} rounded-full overflow-hidden mt-2`}>
                                <div
                                  className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-300"
                                  style={{ width: `${file.progress}%` }}
                                />
                              </div>
                            )}
                            {file.progress < 100 && file.status === 'processing' && (
                              <div className="text-xs mt-1 text-right">
                                {file.progress}%
                              </div>
                            )}
                          </div>

                          <div className={`flex items-center gap-1.5 text-xs ${getStatusColor(file.status)}`}>
                            <div className="w-1.5 h-1.5 rounded-full bg-current"></div>
                            {getStatusText(file.status)}
                          </div>

                          <button
                            onClick={() => removeFile(file.id)}
                            className={`w-8 h-8 rounded border-none cursor-pointer text-base transition-all duration-200 flex-shrink-0 ${'bg-red-900/50 text-red-400 hover:bg-red-900/70'}`}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
              {/* Modal Actions */}
              <div className={`flex gap-3 justify-end pt-6 border-t ${'border-gray-700'}`}>
                <button
                  onClick={() => toggleChat()}
                  className={`px-6 py-3 border rounded-lg text-sm font-medium transition-all duration-200 ${'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadSubmit}
                  disabled={uploadedFiles.filter(f => f.status === 'ready').length === 0 || isUploading || isDownloadingGoogleDriveFiles || isDownloadingOneDriveFiles}
                  className="px-6 py-3 themeSecondary text-white rounded-lg text-sm font-medium transition-all duration-200 hover:shadow-lg hover:transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none flex items-center gap-2"
                >
                  {isUploading ? (
                    <>
                      <Loader size={16} className="animate-spin mr-1" />
                      Uploading...
                    </>
                  ) : (
                    "Analyze & Upload"
                  )}
                </button>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default UploadTrainingStats;